import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
} from '@dnd-kit/sortable'
import { CircleHelp } from 'lucide-react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { RaidPlanAssignmentEntity } from 'app/frontend/types/custom/RaidPlan'
import { getAssignmentEntityLabel } from './assignmentEntityLabels'
import AssignmentDraggableEntityChip from './AssignmentDraggableEntityChip'
import AssignmentEntityChip from './AssignmentEntityChip'
import AssignmentEntityGrid from './AssignmentEntityGrid'
import AssignmentGroupDropTarget from './AssignmentGroupDropTarget'
import AssignmentPaletteEntityButton from './AssignmentPaletteEntityButton'
import { AssignmentPaletteEntity } from './assignmentPaletteEntities'
import getAssignmentEntityIdentity from './assignmentEntityIdentity'
import { canAddAssignmentEntity } from './assignmentScopeMutations'
import AssignmentExpectedEntitiesCount from './AssignmentExpectedEntitiesCount'
import { getStoredSlotWeight } from './assignmentEntityCounting'
import SpecificCharacterFilterMenu from './SpecificCharacterFilterMenu'
import useSpecificCharacterFilters, {
  getSourceCategoryEntries,
  groupPaletteEntitiesByCategory,
} from './useSpecificCharacterFilters'

type DraftEntity = {
  id: string
  entity: RaidPlanAssignmentEntity
}

type DraftEntityLocation = {
  groupIndex: number
  index: number
  item: DraftEntity
}

type Props = {
  spellId: number
  entitiesByGroup: RaidPlanAssignmentEntity[][]
  expectedEntitiesByGroup?: (number | undefined)[]
  paletteEntities: AssignmentPaletteEntity[]
  assignmentUsageCountByIdentity: Record<string, number>
  canEdit: boolean
  onSave: (entitiesByGroup: RaidPlanAssignmentEntity[][]) => void
  onOpenChange: (open: boolean) => void
  trigger?: ReactNode
  open?: boolean
  targetLabel?: string
  allowRepeatedMembers?: boolean
}

const getSortableId = (id: string) => `draft-entity-${id}`

const parseSortableId = (id: string) => {
  const match = id.match(/^draft-entity-(.+)$/)
  if (!match) return null
  return match[1]
}

const getGroupDropTargetIndex = (id: string) => {
  const match = id.match(/^group-drop-(\d+)$/)
  if (!match) return null
  const groupIndex = Number(match[1])
  if (Number.isNaN(groupIndex)) return null
  return groupIndex
}

const findDraftEntityLocation = (
  groups: DraftEntity[][],
  draftEntityId: string,
): DraftEntityLocation | null => {
  for (let groupIndex = 0; groupIndex < groups.length; groupIndex += 1) {
    const index = groups[groupIndex].findIndex(
      (item) => item.id === draftEntityId,
    )
    if (index >= 0) {
      return {
        groupIndex,
        index,
        item: groups[groupIndex][index],
      }
    }
  }
  return null
}

const AssignmentBoxConfigurator = ({
  spellId,
  entitiesByGroup,
  expectedEntitiesByGroup,
  paletteEntities,
  assignmentUsageCountByIdentity,
  canEdit,
  onSave,
  onOpenChange,
  trigger,
  open,
  targetLabel,
  allowRepeatedMembers = false,
}: Props) => {
  const { t } = useTranslation()
  const filterStorageKey = `team-plan-assignment-source-filters:${spellId}`
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = open !== undefined
  const resolvedOpen = isControlled ? open : uncontrolledOpen
  const setResolvedOpen = (nextOpen: boolean) => {
    if (!isControlled) setUncontrolledOpen(nextOpen)
    onOpenChange(nextOpen)
  }
  const [activeGroupIndex, setActiveGroupIndex] = useState(0)
  const [activeDragEntityId, setActiveDragEntityId] = useState<string | null>(
    null,
  )
  const draftEntityIdCounter = useRef(0)
  const createDraftEntity = (
    entity: RaidPlanAssignmentEntity,
  ): DraftEntity => ({
    id: String(draftEntityIdCounter.current++),
    entity,
  })
  const buildDraftEntitiesByGroup = (
    sourceGroups: RaidPlanAssignmentEntity[][],
  ): DraftEntity[][] =>
    sourceGroups.map((group) => group.map(createDraftEntity))
  const [draftEntitiesByGroup, setDraftEntitiesByGroup] = useState<
    DraftEntity[][]
  >(buildDraftEntitiesByGroup(entitiesByGroup))
  const paletteLabelByIdentity = new Map(
    paletteEntities.map((entry) => [
      getAssignmentEntityIdentity(entry.entity),
      entry.label,
    ]),
  )
  const paletteEntitiesByCategory = useMemo(
    () => groupPaletteEntitiesByCategory(paletteEntities),
    [paletteEntities],
  )
  const specificCharacterEntries =
    paletteEntitiesByCategory.specific_character ?? []
  const hasUnfilteredSpecificCharacterEntries = specificCharacterEntries.some(
    (entry) => {
      const rankId = entry.specificCharacterFilter?.teamRankId
      return rankId !== null && rankId !== undefined
    },
  )
  const {
    specificCharacterFilters,
    setSpecificCharacterFilters,
    availableTeamRanks,
    defaultEnabledTeamRankIds,
    enabledTeamRankIdSet,
    hasSpecificCharacterFilterOverrides,
    isSpecificCharacterVisible,
  } = useSpecificCharacterFilters(filterStorageKey, specificCharacterEntries)
  const sourceCategoryEntries = getSourceCategoryEntries(
    hasUnfilteredSpecificCharacterEntries,
  )
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 4,
      },
    }),
  )

  useEffect(() => {
    if (!resolvedOpen) return
    setDraftEntitiesByGroup(buildDraftEntitiesByGroup(entitiesByGroup))
    setActiveGroupIndex(0)
  }, [resolvedOpen])

  const handleDragStart = (event: DragStartEvent) => {
    if (!canEdit) return
    const draftEntityId = parseSortableId(String(event.active.id))
    if (!draftEntityId) return
    setActiveDragEntityId(draftEntityId)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragEntityId(null)
    if (!canEdit) return
    const activeDraftEntityId = parseSortableId(String(event.active.id))
    if (!activeDraftEntityId) return
    const overId = event.over?.id ? String(event.over.id) : ''
    const overDraftEntityId = parseSortableId(overId)
    const groupDropTargetIndex = getGroupDropTargetIndex(overId)
    if (!overDraftEntityId && groupDropTargetIndex === null) return
    setDraftEntitiesByGroup((prev) => {
      const activeLocation = findDraftEntityLocation(prev, activeDraftEntityId)
      if (!activeLocation) return prev
      const next = prev.map((group) => [...group])
      const canMoveToGroup = (groupIndex: number) =>
        canAddAssignmentEntity(activeLocation.item.entity, {
          groupIdentities: new Set(
            next[groupIndex].map((item) =>
              getAssignmentEntityIdentity(item.entity),
            ),
          ),
          scopeIdentities: new Set<string>(),
          allowRepeatedMembers,
        })
      if (overDraftEntityId) {
        const overLocation = findDraftEntityLocation(prev, overDraftEntityId)
        if (!overLocation) return prev
        if (activeLocation.groupIndex === overLocation.groupIndex) {
          if (activeLocation.index === overLocation.index) return prev
          next[activeLocation.groupIndex] = arrayMove(
            next[activeLocation.groupIndex],
            activeLocation.index,
            overLocation.index,
          )
          return next
        }
        if (!canMoveToGroup(overLocation.groupIndex)) return prev
        const [moved] = next[activeLocation.groupIndex].splice(
          activeLocation.index,
          1,
        )
        if (!moved) return prev
        next[overLocation.groupIndex].push(moved)
        setActiveGroupIndex(overLocation.groupIndex)
        return next
      }
      if (groupDropTargetIndex === null) return prev
      if (groupDropTargetIndex === activeLocation.groupIndex) return prev
      if (!canMoveToGroup(groupDropTargetIndex)) return prev
      const [moved] = next[activeLocation.groupIndex].splice(
        activeLocation.index,
        1,
      )
      if (!moved) return prev
      next[groupDropTargetIndex].push(moved)
      setActiveGroupIndex(groupDropTargetIndex)
      return next
    })
  }

  if (!canEdit && !trigger) return null
  if (!canEdit) return <>{trigger}</>
  const normalizedGroups =
    draftEntitiesByGroup.length > 0 ? draftEntitiesByGroup : [[]]
  const safeActiveGroupIndex = Math.min(
    Math.max(activeGroupIndex, 0),
    normalizedGroups.length - 1,
  )
  const existingEntityKeys = new Set(
    normalizedGroups
      .flat()
      .map((item) => getAssignmentEntityIdentity(item.entity)),
  )
  const activeGroupEntityKeys = new Set(
    (normalizedGroups[safeActiveGroupIndex] ?? []).map((item) =>
      getAssignmentEntityIdentity(item.entity),
    ),
  )
  const savedEntityCountByIdentity = entitiesByGroup
    .flat()
    .reduce<Record<string, number>>((acc, entity) => {
      const identity = getAssignmentEntityIdentity(entity)
      acc[identity] = (acc[identity] ?? 0) + 1
      return acc
    }, {})
  const draftEntityCountByIdentity = normalizedGroups
    .flat()
    .reduce<Record<string, number>>((acc, item) => {
      const identity = getAssignmentEntityIdentity(item.entity)
      acc[identity] = (acc[identity] ?? 0) + 1
      return acc
    }, {})
  const activeDragEntityLocation = activeDragEntityId
    ? findDraftEntityLocation(normalizedGroups, activeDragEntityId)
    : null
  const activeDragEntity = activeDragEntityLocation?.item

  return (
    <Dialog
      open={resolvedOpen}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          setDraftEntitiesByGroup(buildDraftEntitiesByGroup(entitiesByGroup))
          setActiveGroupIndex(0)
        }
        setResolvedOpen(nextOpen)
      }}
    >
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        className='max-w-[70rem] gap-1'
        onPointerDownOutside={(event) => event.preventDefault()}
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogTitle>
          {targetLabel
            ? t('dashboard.team.plans.assignments.add-sources-for', {
                target: targetLabel,
              })
            : t('dashboard.team.plans.assignments.add-sources')}
        </DialogTitle>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDragEntityId(null)}
        >
          <div className='space-y-3'>
            {sourceCategoryEntries.map(([category, titleKey]) => {
              const sortedCategoryEntities = (
                paletteEntitiesByCategory[category] ?? []
              ).filter((entry) => {
                if (category !== 'specific_character') return true
                return isSpecificCharacterVisible(entry)
              })
              if (
                category !== 'specific_character' &&
                !sortedCategoryEntities.length
              ) {
                return null
              }
              return (
                <div key={category} className='space-y-1.5'>
                  <div className='text-xs text-muted-foreground inline-flex items-center gap-1'>
                    <span>{t(titleKey)}</span>
                    {category === 'specific_character' ? (
                      <>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='inline-flex'>
                              <CircleHelp className='h-3.5 w-3.5' />
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            {t(
                              'dashboard.team.plans.assignments.categories.specific-character-tooltip',
                            )}
                          </TooltipContent>
                        </Tooltip>
                        <SpecificCharacterFilterMenu
                          filters={specificCharacterFilters}
                          onFiltersChange={setSpecificCharacterFilters}
                          availableTeamRanks={availableTeamRanks}
                          defaultEnabledTeamRankIds={defaultEnabledTeamRankIds}
                          enabledTeamRankIdSet={enabledTeamRankIdSet}
                          hasOverrides={hasSpecificCharacterFilterOverrides}
                        />
                      </>
                    ) : null}
                  </div>
                  <div className='flex flex-wrap gap-1.5'>
                    {sortedCategoryEntities.length === 0 &&
                    category === 'specific_character' ? (
                      <div className='text-xs text-muted-foreground'>
                        {t(
                          'dashboard.team.plans.assignments.categories.specific-character-empty',
                        )}
                      </div>
                    ) : (
                      sortedCategoryEntities.map((entry) => {
                        const entryIdentity = getAssignmentEntityIdentity(
                          entry.entity,
                        )
                        const assignmentCount = Math.max(
                          0,
                          (assignmentUsageCountByIdentity[entryIdentity] ?? 0) -
                            (savedEntityCountByIdentity[entryIdentity] ?? 0) +
                            (draftEntityCountByIdentity[entryIdentity] ?? 0),
                        )
                        const isAlreadyAdded = !canAddAssignmentEntity(
                          entry.entity,
                          {
                            groupIdentities: activeGroupEntityKeys,
                            scopeIdentities: existingEntityKeys,
                            allowRepeatedMembers,
                          },
                        )
                        const isHighlighted =
                          isAlreadyAdded ||
                          (allowRepeatedMembers &&
                            entry.entity.kind === 'member' &&
                            activeGroupEntityKeys.has(entryIdentity))
                        return (
                          <AssignmentPaletteEntityButton
                            key={entry.id}
                            label={entry.label}
                            assignmentCount={assignmentCount}
                            category={entry.category}
                            disabled={!canEdit}
                            highlighted={isHighlighted}
                            onClick={() => {
                              setDraftEntitiesByGroup((prev) => {
                                const next = prev.map((group) => [...group])
                                const activeEntities =
                                  next[safeActiveGroupIndex] ?? []
                                if (isAlreadyAdded) {
                                  if (entry.entity.kind === 'member') {
                                    next[safeActiveGroupIndex] =
                                      activeEntities.filter(
                                        (item) =>
                                          getAssignmentEntityIdentity(
                                            item.entity,
                                          ) !== entryIdentity,
                                      )
                                    return next
                                  }
                                  for (
                                    let groupIndex = 0;
                                    groupIndex < next.length;
                                    groupIndex += 1
                                  ) {
                                    next[groupIndex] = next[groupIndex].filter(
                                      (item) =>
                                        getAssignmentEntityIdentity(
                                          item.entity,
                                        ) !== entryIdentity,
                                    )
                                  }
                                  return next
                                }
                                next[safeActiveGroupIndex] = [
                                  ...activeEntities,
                                  createDraftEntity(entry.entity),
                                ]
                                return next
                              })
                            }}
                          />
                        )
                      })
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          <div
            className={cn('grid gap-2 mt-2 pt-2 border-t border-border')}
            style={{
              gridTemplateColumns: `repeat(${Math.max(
                Math.min(normalizedGroups.length, 4),
                1,
              )}, minmax(0, 1fr))`,
            }}
          >
            {normalizedGroups.map((groupEntities, groupIndex) => {
              const isActive = groupIndex === safeActiveGroupIndex
              const slotWeight = getStoredSlotWeight(
                groupEntities.map((item) => item.entity),
              )
              return (
                <div key={`group-${groupIndex}`}>
                  <div className='mb-1 flex items-center justify-between gap-2'>
                    <span className='flex items-center gap-2 text-xs text-muted-foreground'>
                      {normalizedGroups.length > 1
                        ? t('dashboard.team.plans.assignments.group-label', {
                            count: groupIndex + 1,
                          })
                        : 'Selected entities'}
                      <AssignmentExpectedEntitiesCount
                        actual={slotWeight.weight}
                        expected={expectedEntitiesByGroup?.[groupIndex]}
                        disabled={slotWeight.disabling}
                      />
                    </span>
                    <Button
                      type='button'
                      variant='outline'
                      size='xs'
                      onClick={() =>
                        setDraftEntitiesByGroup((prev) => {
                          const next = prev.map((group) => [...group])
                          next[groupIndex] = []
                          return next
                        })
                      }
                      disabled={groupEntities.length === 0}
                    >
                      {t('dashboard.team.plans.assignments.clear-all')}
                    </Button>
                  </div>
                  <AssignmentGroupDropTarget
                    id={`group-drop-${groupIndex}`}
                    className={cn(
                      'border-dashed p-2 min-h-12',
                      normalizedGroups.length > 1
                        ? isActive
                          ? 'border-primary'
                          : 'cursor-pointer hover:border-primary/50'
                        : '',
                    )}
                    onClick={() => setActiveGroupIndex(groupIndex)}
                  >
                    {groupEntities.length === 0 ? (
                      typeof expectedEntitiesByGroup?.[groupIndex] ===
                      'number' ? null : (
                        <div className='text-xs text-muted-foreground'>
                          {isActive
                            ? t('dashboard.team.plans.assignments.drop-hint')
                            : t(
                                'dashboard.team.plans.assignments.inactive-drop-hint',
                              )}
                        </div>
                      )
                    ) : (
                      <SortableContext
                        items={groupEntities.map((item) =>
                          getSortableId(item.id),
                        )}
                        strategy={rectSortingStrategy}
                      >
                        <AssignmentEntityGrid>
                          {groupEntities.map((item, entityIndex) => (
                            <div key={item.id} className='min-w-0'>
                              <AssignmentDraggableEntityChip
                                sortableId={getSortableId(item.id)}
                                label={
                                  paletteLabelByIdentity.get(
                                    getAssignmentEntityIdentity(item.entity),
                                  ) ?? getAssignmentEntityLabel(t, item.entity)
                                }
                                index={entityIndex}
                                canEdit={canEdit}
                                onRemove={() =>
                                  setDraftEntitiesByGroup((prev) => {
                                    const next = prev.map((group) => [...group])
                                    next[groupIndex] = next[groupIndex].filter(
                                      (groupItem) => groupItem.id !== item.id,
                                    )
                                    return next
                                  })
                                }
                              />
                            </div>
                          ))}
                        </AssignmentEntityGrid>
                      </SortableContext>
                    )}
                  </AssignmentGroupDropTarget>
                </div>
              )
            })}
          </div>
          {typeof document !== 'undefined'
            ? createPortal(
                <DragOverlay>
                  {activeDragEntity ? (
                    <AssignmentEntityChip
                      index={activeDragEntityLocation?.index ?? 0}
                      label={
                        paletteLabelByIdentity.get(
                          getAssignmentEntityIdentity(activeDragEntity.entity),
                        ) ??
                        getAssignmentEntityLabel(t, activeDragEntity.entity)
                      }
                    />
                  ) : null}
                </DragOverlay>,
                document.body,
              )
            : null}
        </DndContext>

        <div className='flex items-center justify-end gap-2 mt-3'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              setDraftEntitiesByGroup(
                buildDraftEntitiesByGroup(entitiesByGroup),
              )
              setActiveGroupIndex(0)
              setResolvedOpen(false)
            }}
          >
            {t('shared.cancel')}
          </Button>
          <Button
            type='button'
            onClick={() => {
              onSave(
                normalizedGroups.map((group) =>
                  group.map((item) => item.entity),
                ),
              )
              setResolvedOpen(false)
            }}
          >
            {t('shared.save-changes')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default AssignmentBoxConfigurator
